import React, { useState } from "react";

import {
  campaignOptionsType,
  kpiChoicesEnum,
  kpiOptionsType,
  tacticOptionsType,
  costMetricOptionsType,
  CostOptionsEnum,
  tacticNameType,
} from "./OptimusChartBox";
import { OptimusLineChart } from "./optimusLineChart";
import { OptimusCampaignData } from "../../../../rest/rest";
import { ApolloError, useQuery } from "@apollo/client";
import { OptimusDataGrid } from "./optimusDataGrid";
import {
  GET_ITEM_BUNDLE_CONTEXT,
  GET_OPTIMUS_OPTIMISATION_LOGS,
} from "../../../../graphql/operations/campaignOperations/operations";
import { ComponentLoading } from "../../../../components/loading/ComponentLoading";
import { SectionBox } from "../../../../components/sectionBox/SectionBox";
import {
  DspChoices,
  OptimisationLogType,
  SegmentsBundleContextLineTypeChoices,
  SegmentBundleContextHandlerType,
} from "../../../../graphql/generated";
import { OptimusChartBox } from "./OptimusChartBox";
import { GET_OPTIMUS_DATA_REPORT_STORE } from "../../../../graphqlExternal/operations/operations";
import { LookbackOptionsEnum, lookbackOptionsType } from "../../../../utils/displayGetters";

export interface OptimisationUserType {
  firstName: string;
  lastName: string;
}


export const Optimus = () => {
  let tacticValues: Array<number | string> = [];
  let campaignsWithContext: campaignOptionsType[] = [];

  const [selectLookback, setSelectLookback] = useState<lookbackOptionsType>({
    label: "Last 30 Days",
    value: LookbackOptionsEnum.Last_30Days,
  });

  const [selectCampaign, setSelectCampaign] = useState<campaignOptionsType>({
    label: "Select Campaign...",
    value: "",
    dsp: DspChoices.Dv360,
    lineType: [],
    relevantIdList: [
      {
        lineType: [SegmentsBundleContextLineTypeChoices.LineItem],
        relevantIdList: [""],
      },
    ],
  });

  const [selectTacticName, setTacticName] = useState<tacticNameType[]>([]);

  const [selectTactic, setSelectTactic] = useState<tacticOptionsType[]>([]);

  const [selectKpi, setSelectKpi] = useState<kpiOptionsType[]>([
    { label: "Impressions", value: kpiChoicesEnum.impressions },
    { label: "CTR", value: kpiChoicesEnum.Ctr },
  ]);

  const [selectCostMetric, setSelectCostMetric] =
    useState<costMetricOptionsType>({
      label: "Media Cost (Adv Currency)",
      value: CostOptionsEnum.media_cost_adv_currency,
    });

  // map date values
  let startDate = new Date("2024-10-20").toISOString().split("T")[0];
  const todayDate = new Date();
  const endDate = todayDate.toISOString().split("T")[0];

  switch (selectLookback.value) {
    case "LAST 7 DAYS":
      todayDate.setDate(todayDate.getDate() - 8);
      startDate = todayDate.toISOString().split("T")[0];
      break;
    case "LAST 30 DAYS":
      todayDate.setDate(todayDate.getDate() - 31);
      startDate = todayDate.toISOString().split("T")[0];
      break;
    case "LAST 90 DAYS":
      todayDate.setDate(todayDate.getDate() - 91);
      startDate = todayDate.toISOString().split("T")[0];
      break;
    default:
      todayDate.setDate(todayDate.getDate() - 31);
      startDate = todayDate.toISOString().split("T")[0];
  }

  // map tactics prior to query execution (line item ids/ ad group ids)
  tacticValues = selectTactic?.map((item) => item.value) ?? [];

  // query reportStore db in new ui
  const {
    data: reportStoreData,
    loading: reportStoreLoading,
    error: reportStoreError,
  } = useQuery(GET_OPTIMUS_DATA_REPORT_STORE, {
    variables: {
      dsp: selectCampaign.dsp,
      lineItemIds: tacticValues,
      startDate: startDate,
      endDate: endDate,
      costColumn: selectCostMetric.value,
    },
    context: { clientName: "illumaDbEndpoint", type: "graphql" },
  });

  // get optimisation logs for a given campaign
  const {
    data: optimisationData,
    loading: optimisationLoading,
    error: optimisationError,
  } = useQuery(GET_OPTIMUS_OPTIMISATION_LOGS, {
    variables: {
      campaignId: selectCampaign.value,
      startDate: startDate,
      endDate: endDate,
    },
  });

  // update campaign data filter for camapaigns with bundle context

  const {
    data: campaignContextData,
    loading: campaignContextLoading,
    error: campaignContextError,
  } = useQuery(GET_ITEM_BUNDLE_CONTEXT, {
    variables: {
      campaignId: null,
    },
  });

  if (campaignContextData) {
    // map campaigns with context data if not already in campaignsWithContext
    campaignsWithContext = campaignContextData.segmentContextItem.reduce(
      (acc: campaignOptionsType[], bundle: SegmentBundleContextHandlerType) => {
        const { __typename, ...cleanedCampaign } = bundle.campaign;
        const existing = acc.find((item) => item.value === cleanedCampaign.id);

        if (!existing) {
          acc.push({
            label: cleanedCampaign.name,
            value: cleanedCampaign.id,
            relevantIdList: [
              {
                lineType: [bundle.lineType],
                relevantIdList: bundle.relevantIds,
              },
            ],
            lineType: [bundle.lineType],
            dsp: cleanedCampaign.dspInfo.dspName,
          });
        } else {
          existing.relevantIdList.push({
            lineType: [bundle.lineType],
            relevantIdList: bundle.relevantIds,
          });
          existing.lineType.push(bundle.lineType);
        }
        return acc;
      },
      [] as campaignOptionsType[]
    );
  }

  if (campaignContextLoading)
    return (
      <SectionBox>
        <ComponentLoading text={"Loading Chart..."} p={5} />
      </SectionBox>
    );

  if (optimisationError) {
    const networkError = optimisationError.networkError as any;
    console.error(
      "Apollo useQuery error:",
      networkError?.result || optimisationError.message
    );
    return <p>Optimisation Log Error: {optimisationError.message}</p>;
  }
  if (campaignContextError) {
    {
      return <SectionBox>There seems to be a problem</SectionBox>;
    }
  }

  return (
    <>
      <OptimusChartBox
        chartBoxHeader={`OPTIMUS PERFORMANCE: ${selectCampaign.label}`}
        lookback={{
          selectLookback: selectLookback,
          setSelectLookback: setSelectLookback,
        }}
        campaign={{
          selectCampaign: selectCampaign,
          setSelectCampaign: setSelectCampaign,
        }}
        tacticName={{
          selectTacticName: selectTacticName,
          setTacticName: setTacticName,
        }}
        campaignsWithContext={campaignsWithContext}
        tactic={{
          selectTactic: selectTactic,
          setSelectTactic: setSelectTactic,
        }}
        kpi={{ selectKpi: selectKpi, setSelectKpi: setSelectKpi }}
        style={{
          flexWrap: "wrap",
          margin: "10px",
          padding: "5px",
          gap: "10px 10px",
        }}
        costMetric={{
          selectCostMetric: selectCostMetric,
          setSelectCostMetric: setSelectCostMetric,
        }}
      >
        <div style={{ display: "flex", justifyContent: "center" }}>
          {reportStoreData &&
            renderChartContext({
              campaignData: reportStoreData.reportStoreCampaign.campaign_data,
              error: reportStoreError,
              optimisationData,
              loading: reportStoreLoading,
              selectKpi,
            })}
        </div>
      </OptimusChartBox>
      {optimisationData && optimisationData.optimisationLogs !== null ? (
        <SectionBox mt={4} mb={0} margin={"10px"} padding="5px">
          {optimisationLoading ? (
            <ComponentLoading text={"Loading Table..."} p={5} />
          ) : (
            <OptimusDataGrid
              optimisations={optimisationData.optimisationLogs}
            />
          )}
        </SectionBox>
      ) : (
        <SectionBox
          mt={4}
          mb={5}
          margin={"10px"}
          padding="5px"
          display="flex"
          justifyContent="center"
        >
          No Optimisation Data for Selected Filters
        </SectionBox>
      )}
    </>
  );
};

export interface renderChartContextProps {
  campaignData?: OptimusCampaignData[];
  error?: ApolloError | undefined;
  optimisationData?: { optimisationLogs: OptimisationLogType[] };
  loading?: boolean;
  selectKpi?: kpiOptionsType[];
}
const renderChartContext = ({
  campaignData,
  error,
  optimisationData,
  loading,
  selectKpi,
}: renderChartContextProps) => {
  if (campaignData && campaignData !== null && !error) {
    return (
      <>
        <OptimusLineChart
          optimusCampaignData={campaignData}
          optimisations={optimisationData?.optimisationLogs}
          kpi={selectKpi}
        />
      </>
    );
  } else if (loading) {
    return (
      <SectionBox>
        <ComponentLoading text={"Loading Chart..."} p={5} />
      </SectionBox>
    );
  } else {
    return <div>{"No Performance Data Available for Selected filters"}</div>;
  }
};
